'use client';

import { Box, ClickAwayListener, Popper, useMediaQuery } from '@mui/material';
import { animated, useSpring } from '@react-spring/web';
import { useCallback, useEffect, useRef, useState } from 'react';

import { usePopupStack } from '../../hooks/usePopupStack';
import type { AnchoredPopupProps, PopperModifier } from './types';
import { toPopperPlacement } from './types';

/**
 * A smart popup component that automatically positions itself based on available space.
 *
 * @example
 * ```tsx
 * <AnchoredPopup
 *   anchorEl={buttonRef.current}
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   placement="left-end"
 *   width={400}
 * >
 *   <MyContent />
 * </AnchoredPopup>
 * ```
 */
export function AnchoredPopup({
  anchorEl,
  open,
  onClose,
  children,
  id,
  placement = 'right-start',
  width = 'auto',
  maxHeight = 'none',
  disablePortal = false,
  ariaLabel,
  zIndex: zIndexProp = 1300,
  useStackContext = true,
  onStackRegistered,
  offset = 8,
  animated: enableAnimation = true,
  onOpenComplete,
  reserveEndWidth = 0,
}: AnchoredPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);
  const [actualPlacement, setActualPlacement] = useState<string>(placement);
  const [shouldRender, setShouldRender] = useState(open);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // Use popup stack context for z-index management
  // Only register when both useStackContext is enabled AND popup is open
  const {
    zIndex: stackZIndex,
    getChildZIndex,
    popupId,
  } = usePopupStack(onClose, useStackContext && open);

  // Determine final z-index: use stack context if enabled, otherwise use prop
  const zIndex = useStackContext ? stackZIndex : zIndexProp;

  // Notify parent when stack registration happens
  // Only call after we have a valid popupId (meaning we're registered with correct z-index)
  // Always update when getChildZIndex changes to ensure parent has latest function
  useEffect(() => {
    if (useStackContext && onStackRegistered && popupId) {
      onStackRegistered(getChildZIndex);
    }
  }, [useStackContext, onStackRegistered, getChildZIndex, popupId]);

  // Keep rendered during exit animation
  useEffect(() => {
    if (open) {
      setShouldRender(true);
    }
  }, [open]);

  // Build modifiers for smart positioning
  const modifiers: PopperModifier[] = [
    {
      name: 'flip',
      enabled: true,
      options: {
        fallbackPlacements: getFallbackPlacements(placement),
        rootBoundary: 'document',
        padding: 16,
      },
    },
    {
      name: 'preventOverflow',
      enabled: true,
      options: {
        altAxis: true,
        tether: false, // Allow free shifting to stay within bounds
        boundary: 'clippingParents',
        rootBoundary: 'viewport',
        // Add reserveEndWidth to right padding to account for sub-panels
        padding: { top: 16, right: 16 + reserveEndWidth, bottom: 16, left: 16 },
      },
    },
    {
      name: 'offset',
      enabled: true,
      options: {
        offset: [0, offset],
      },
    },
    {
      name: 'onUpdate',
      enabled: true,
      phase: 'afterWrite',
      fn: ({ state }) => {
        // Track actual placement after flip modifier runs
        if (state.placement !== actualPlacement) {
          setActualPlacement(state.placement);
        }
      },
    },
  ];

  // Handle escape key
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Focus management
  useEffect(() => {
    if (open && popupRef.current) {
      const timer = setTimeout(() => {
        popupRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Handle click away
  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      // Don't close if clicking the anchor element
      if (anchorEl && anchorEl.contains(event.target as Node)) {
        return;
      }
      onClose();
    },
    [onClose, anchorEl],
  );

  // Spring animation - skip when animations disabled or user prefers reduced motion
  const springStyle = useSpring({
    scale: open ? 1 : 0.5,
    opacity: open ? 1 : 0,
    brightness: open ? 100 : 95,
    config: {
      tension: 400,
      friction: 32,
      precision: 0.01,
    },
    immediate: !enableAnimation || prefersReducedMotion,
    onRest: () => {
      if (open) {
        onOpenComplete?.();
      } else {
        setShouldRender(false);
      }
    },
  });

  // Compute dimension values
  const widthValue = typeof width === 'number' ? `${width}px` : width;
  const heightValue =
    maxHeight !== 'none' ? (typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight) : 'auto';

  // Box styling
  const boxSx = {
    width: widthValue,
    minWidth: widthValue,
    height: heightValue,
    backgroundColor: 'background.default',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    outline: 'none',
    overflow: 'hidden',
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <Popper
      open={shouldRender}
      anchorEl={anchorEl}
      placement={toPopperPlacement(placement)}
      disablePortal={disablePortal}
      modifiers={modifiers}
      style={{ zIndex }}
    >
      <animated.div
        style={{
          transformOrigin: getTransformOrigin(actualPlacement),
          transform: springStyle.scale.to((s) => `scale(${s})`),
          opacity: springStyle.opacity,
          filter: springStyle.brightness.to((b) => `brightness(${b}%)`),
          height: '100%',
          display: 'flex',
          width: widthValue,
          minWidth: widthValue,
        }}
      >
        <ClickAwayListener
          onClickAway={handleClickAway}
          mouseEvent="onMouseDown"
        >
          <Box
            ref={popupRef}
            id={id}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            tabIndex={-1}
            sx={boxSx}
          >
            {children}
          </Box>
        </ClickAwayListener>
      </animated.div>
    </Popper>
  );
}

/**
 * Get fallback placements for the flip modifier based on the preferred placement.
 * For right-side placements, falls back to left-side first.
 * For left-side placements, falls back to right-side first.
 */
type Placement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

/**
 * Get fallback placements for the flip modifier.
 *
 * Priority:
 * 1. Same side, different alignment (change growth direction)
 * 2. Opposite side with same logic
 * 3. Perpendicular axis (top/bottom for horizontal, left/right for vertical)
 */
function getFallbackPlacements(placement: string): Placement[] {
  switch (placement) {
    // Right placements - stay on right, then try left, then vertical
    case 'right-start':
      return ['right-end', 'right', 'left-start', 'left-end', 'left', 'bottom-start', 'top-start'];
    case 'right-end':
      return ['right-start', 'right', 'left-end', 'left-start', 'left', 'bottom-end', 'top-end'];
    case 'right':
      return ['right-start', 'right-end', 'left', 'left-start', 'left-end', 'bottom', 'top'];

    // Left placements - stay on left, then try right, then vertical
    case 'left-start':
      return ['left-end', 'left', 'right-start', 'right-end', 'right', 'bottom-start', 'top-start'];
    case 'left-end':
      return ['left-start', 'left', 'right-end', 'right-start', 'right', 'bottom-end', 'top-end'];
    case 'left':
      return ['left-start', 'left-end', 'right', 'right-start', 'right-end', 'bottom', 'top'];

    // Bottom placements - stay on bottom, then try top, then horizontal
    case 'bottom-start':
      return ['bottom-end', 'bottom', 'top-start', 'top-end', 'top', 'right-start', 'left-start'];
    case 'bottom-end':
      return ['bottom-start', 'bottom', 'top-end', 'top-start', 'top', 'right-end', 'left-end'];
    case 'bottom':
      return ['bottom-start', 'bottom-end', 'top', 'top-start', 'top-end', 'right', 'left'];

    // Top placements - stay on top, then try bottom, then horizontal
    case 'top-start':
      return [
        'top-end',
        'top',
        'bottom-start',
        'bottom-end',
        'bottom',
        'right-start',
        'left-start',
      ];
    case 'top-end':
      return ['top-start', 'top', 'bottom-end', 'bottom-start', 'bottom', 'right-end', 'left-end'];
    case 'top':
      return ['top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end', 'right', 'left'];

    default:
      return ['bottom', 'top', 'left', 'right'];
  }
}

/**
 * Get transform origin based on placement so the popup appears to emerge from the anchor.
 */
function getTransformOrigin(placement: string): string {
  switch (placement) {
    // Right placements - origin on left edge (toward anchor)
    case 'right-start':
      return 'left top';
    case 'right-end':
      return 'left bottom';
    case 'right':
      return 'left center';

    // Left placements - origin on right edge (toward anchor)
    case 'left-start':
      return 'right top';
    case 'left-end':
      return 'right bottom';
    case 'left':
      return 'right center';

    // Bottom placements - origin on top edge (toward anchor)
    case 'bottom-start':
      return 'top left';
    case 'bottom-end':
      return 'top right';
    case 'bottom':
      return 'top center';

    // Top placements - origin on bottom edge (toward anchor)
    case 'top-start':
      return 'bottom left';
    case 'top-end':
      return 'bottom right';
    case 'top':
      return 'bottom center';

    default:
      return 'left top';
  }
}

export default AnchoredPopup;
